import {
  type OcenaStanowiska,
  type Powaga,
  type StanowiskoDoOceny,
  ocenStanowisko,
  podsumujFlote,
  posortujFlote,
} from '@insolitum/fabrica-panel-core';
import {
  AlertTriangle,
  CheckCircle2,
  CircleHelp,
  HelpCircle,
  PowerOff,
  Search,
} from 'lucide-react';
import { useMemo, useState } from 'react';

// =============================================================================
// TABLICA FLOTY — jeden wiersz na stanowisko, najgorsze na górze.
//
// Ten komponent NIE POBIERA NICZEGO. Dostaje listę i najnowszy numer wydania,
// a skąd one przyjechały, decyduje ekran: dział urządzeń podaje licencje,
// które lista i tak już ma w pamięci (zero dodatkowych zapytań), a ekran
// platformy — wynik `fabrica_flota_paneli` z całej bazy. Ten sam werdykt
// i ten sam układ w obu miejscach; inaczej „usterka" znaczyłaby co innego
// u klienta niż u nas.
//
// CZEGO TU NIE MA: wykresów, procentów dostępności i średnich. Średnia po
// flocie to liczba, która wygląda dobrze przy jednym zepsutym stanowisku na
// dwadzieścia — a to jedno zepsute jest całą treścią tego ekranu.
// =============================================================================

const OPIS_POWAGI: Record<Powaga, { napis: string; klasa: string; ikona: typeof CheckCircle2 }> = {
  usterka: { napis: 'usterka', klasa: 'text-red-400', ikona: AlertTriangle },
  uwaga: { napis: 'uwaga', klasa: 'text-amber-400', ikona: AlertTriangle },
  niewiadomo: { napis: 'nie podaje', klasa: 'text-aurora-subtle', ikona: HelpCircle },
  sprawne: { napis: 'sprawne', klasa: 'text-emerald-400', ikona: CheckCircle2 },
  nieczynne: { napis: 'nieczynne', klasa: 'text-aurora-subtle', ikona: PowerOff },
};

function czasPoLudzku(sekundy: number | null): string {
  if (sekundy === null) return 'nigdy';
  if (sekundy < 90) return 'przed chwilą';
  if (sekundy < 3600) return `${Math.round(sekundy / 60)} min temu`;
  if (sekundy < 86_400) return `${Math.round(sekundy / 3600)} h temu`;
  return `${Math.round(sekundy / 86_400)} d temu`;
}

export interface FlotaPaneliProps {
  stanowiska: StanowiskoDoOceny[];
  /** Najwyższy opublikowany `version_code` panelu; `null` = rejestr milczy. */
  najnowszyKodWersji: number | null;
  /** Czy pokazywać kolumnę organizacji (ekran platformy — tak, dział — nie). */
  zOrganizacja?: boolean;
  laduje?: boolean;
  /** Kliknięcie wiersza. Bez tego wiersz nie jest klikalny — i nie udaje, że jest. */
  naWybor?: (stanowisko: StanowiskoDoOceny) => void;
}

export function FlotaPaneli({
  stanowiska,
  najnowszyKodWersji,
  zOrganizacja = false,
  laduje = false,
  naWybor,
}: FlotaPaneliProps) {
  const [szukaj, setSzukaj] = useState('');
  const [filtr, setFiltr] = useState<Powaga | 'wszystkie'>('wszystkie');

  // `teraz` liczone RAZ na render, nie w każdym wierszu. Dwadzieścia cztery
  // wywołania `Date.now()` w jednej pętli dają dwadzieścia cztery różne
  // chwile odniesienia — i wiersz, który w sortowaniu jest starszy od
  // sąsiada, a w kolumnie „ostatni puls" młodszy.
  const oceny = useMemo<OcenaStanowiska[]>(() => {
    const teraz = Date.now();
    return posortujFlote(stanowiska.map((s) => ocenStanowisko(s, { teraz, najnowszyKodWersji })));
  }, [stanowiska, najnowszyKodWersji]);

  const podsumowanie = useMemo(() => podsumujFlote(oceny), [oceny]);

  const widoczne = useMemo(() => {
    const igla = szukaj.trim().toLowerCase();
    return oceny.filter((o) => {
      if (filtr !== 'wszystkie' && o.powaga !== filtr) return false;
      if (!igla) return true;
      const s = o.stanowisko;
      return [s.nazwa, s.license_code, s.organizacja, s.puls?.wersja, s.puls?.system]
        .filter((x): x is string => typeof x === 'string')
        .some((x) => x.toLowerCase().includes(igla));
    });
  }, [oceny, filtr, szukaj]);

  if (laduje) {
    return (
      <div className="space-y-2">
        <div className="skel h-10" />
        <div className="skel h-10" />
        <div className="skel h-10" />
      </div>
    );
  }

  if (stanowiska.length === 0) {
    return (
      <p className="text-sm text-aurora-subtle">
        Żadne stanowisko nie jest jeszcze sparowane — flota zaczyna istnieć przy pierwszym pulsie.
      </p>
    );
  }

  const kubelki: Array<{ klucz: Powaga | 'wszystkie'; napis: string; ile: number }> = [
    { klucz: 'wszystkie', napis: 'wszystkie', ile: oceny.length },
    { klucz: 'usterka', napis: 'usterki', ile: podsumowanie.usterka },
    { klucz: 'uwaga', napis: 'uwagi', ile: podsumowanie.uwaga },
    { klucz: 'niewiadomo', napis: 'nie podają', ile: podsumowanie.niewiadomo },
    { klucz: 'sprawne', napis: 'sprawne', ile: podsumowanie.sprawne },
    { klucz: 'nieczynne', napis: 'nieczynne', ile: podsumowanie.nieczynne },
  ];

  return (
    <div className="space-y-4">
      {/* Liczniki są jednocześnie filtrem — kubełek, którego nie da się kliknąć,
          zmusza do przewijania listy w poszukiwaniu tego, co właśnie policzył. */}
      <div className="flex flex-wrap items-center gap-2">
        {kubelki.map((k) => {
          const wybrany = filtr === k.klucz;
          const pusty = k.ile === 0;
          return (
            <button
              key={k.klucz}
              type="button"
              onClick={() => setFiltr(wybrany ? 'wszystkie' : k.klucz)}
              disabled={pusty && k.klucz !== 'wszystkie'}
              className={`badge ${wybrany ? 'ring-1 ring-aurora-brand' : ''} ${
                pusty ? 'opacity-40' : ''
              }`}
            >
              <span className="mono tabular-nums">{k.ile}</span>
              <span className="ml-1">{k.napis}</span>
            </button>
          );
        })}
        <div className="ml-auto relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-aurora-subtle" />
          <input
            value={szukaj}
            onChange={(e) => setSzukaj(e.target.value)}
            placeholder="nazwa, kod, wersja…"
            className="input pl-8 h-8 text-sm"
            aria-label="Szukaj stanowiska"
          />
        </div>
      </div>

      {/* Tabela przewija się poziomo we WŁASNYM pudełku — strona nie ma prawa
          jechać w bok, bo wtedy znikają liczniki powyżej. */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-aurora-subtle border-b border-aurora-border">
              <th className="py-2 pr-3 font-normal">Stan</th>
              <th className="py-2 pr-3 font-normal">Stanowisko</th>
              {zOrganizacja && <th className="py-2 pr-3 font-normal">Organizacja</th>}
              <th className="py-2 pr-3 font-normal">Co jest nie tak</th>
              <th className="py-2 pr-3 font-normal">Wersja</th>
              <th className="py-2 pr-3 font-normal">Waga</th>
              <th className="py-2 font-normal">Ostatni puls</th>
            </tr>
          </thead>
          <tbody>
            {widoczne.map((o) => {
              const s = o.stanowisko;
              const opis = OPIS_POWAGI[o.powaga];
              const Ikona = opis.ikona;
              const zrodloMasy = s.puls?.waga?.zrodlo;
              return (
                <tr
                  key={s.id}
                  onClick={naWybor ? () => naWybor(s) : undefined}
                  className={`border-b border-aurora-border align-top ${
                    naWybor ? 'cursor-pointer hover:bg-aurora-raised' : ''
                  }`}
                >
                  <td className="py-2 pr-3 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 ${opis.klasa}`}>
                      <Ikona className="w-4 h-4 shrink-0" />
                      {opis.napis}
                    </span>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="text-aurora-text">{s.nazwa || 'Stanowisko bez nazwy'}</div>
                    <div className="mono text-xs text-aurora-subtle break-all">
                      {s.license_code}
                    </div>
                  </td>
                  {zOrganizacja && (
                    <td className="py-2 pr-3 text-aurora-muted">{s.organizacja || '—'}</td>
                  )}
                  <td className="py-2 pr-3">
                    {o.usterki.length > 0 ? (
                      <ul className="space-y-0.5">
                        {o.usterki.map((u) => (
                          <li
                            key={u.klucz}
                            className={u.powaga === 'usterka' ? 'text-red-400' : 'text-amber-400'}
                          >
                            {u.zdanie}
                          </li>
                        ))}
                      </ul>
                    ) : o.powaga === 'nieczynne' ? (
                      <span className="text-aurora-subtle">
                        licencja bez sparowanego urządzenia
                      </span>
                    ) : (
                      <span className="text-aurora-subtle">—</span>
                    )}
                    {/* „Nie podaje" jest osobnym zdaniem, nie brakiem zdania:
                        stara binarka MILCZY o polu, a milczenie wygląda tak
                        samo jak zdrowie dopóki się go nie nazwie. */}
                    {o.niewiadome.length > 0 && (
                      <div className="flex items-start gap-1 text-xs text-aurora-subtle mt-1">
                        <CircleHelp className="w-3 h-3 mt-0.5 shrink-0" />
                        <span>nie podaje: {o.niewiadome.join(', ')}</span>
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    <span className="mono text-aurora-muted">{s.puls?.wersja ?? '—'}</span>
                    {o.wTyle != null && o.wTyle > 0 && (
                      <span className="mono text-xs text-amber-400 ml-1">−{o.wTyle}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {zrodloMasy === undefined ? (
                      <span className="text-aurora-subtle">nie podaje</span>
                    ) : zrodloMasy === 'symulator' ? (
                      <span className="text-red-400">symulator</span>
                    ) : (
                      <span className="text-aurora-muted">{zrodloMasy}</span>
                    )}
                  </td>
                  <td className="py-2 whitespace-nowrap text-aurora-muted tabular-nums">
                    {czasPoLudzku(o.ciszaS)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {widoczne.length === 0 && (
        <p className="text-sm text-aurora-subtle">Żaden wiersz nie pasuje do tego filtru.</p>
      )}
    </div>
  );
}
